import { useEffect, useRef, useState } from 'react'

import type { OnlyFansMessage } from '@api'

import { SafeFormattedText } from '@/components/ui/safe-formatted-message'
import { classNames } from '@/utils/helpers'

import type { FanCountdown } from '../../types'
import { getMessageMediaPreviewType, secondsToTime } from '../utils'
import { MEDIA_TYPE_ICONS } from './consts'

interface Props {
  message?: OnlyFansMessage
  countdown?: FanCountdown
  isChatMuted?: boolean
  isFromCreator?: boolean
  isNew?: boolean
}

export const MessagePreview = ({
  message,
  countdown,
  isChatMuted,
  isFromCreator,
  isNew,
}: Props) => {
  const [seconds, setSeconds] = useState<number>(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const stopTimer = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        setSeconds(0)
      }
    }
    const setTimer = (end: number) => {
      const seconds = Math.floor((end - Date.now()) / 1000)

      if (seconds > 0) {
        setSeconds(seconds)
      } else {
        stopTimer()
      }
    }

    if (countdown) {
      stopTimer()
      setTimer(countdown.end)
      timerRef.current = setInterval(() => {
        setTimer(countdown.end)
      }, 1000)
    } else {
      stopTimer()
    }
  }, [countdown])

  if (!message) {
    return null
  }

  const formatedMessageText = (
    <div
      className={classNames(
        'overflow-hidden text-ellipsis whitespace-nowrap',
        (isChatMuted || isFromCreator) && 'text-secondary-300',
        isNew && 'font-semibold',
      )}
    >
      <SafeFormattedText
        plain
        clickable={false}
        text={message.text!}
      />
    </div>
  )

  let content = formatedMessageText

  const mediaType = getMessageMediaPreviewType(message)

  if (mediaType) {
    const { icon, label } =
      MEDIA_TYPE_ICONS[mediaType as keyof typeof MEDIA_TYPE_ICONS]

    content = (
      <div className="flex gap-2 items-center">
        <span
          className={classNames(
            'flex items-center gap-1',
            (isChatMuted || isFromCreator) && 'text-secondary-300',
            isNew && 'font-semibold',
          )}
        >
          {icon}
        </span>
        {message.text! ? formatedMessageText : 'Media only'}
      </div>
    )
  }

  if (seconds || countdown) {
    content = (
      <div
        className={classNames(
          'rounded-full px-1 leading-[22px]',
          seconds > 60
            ? 'bg-success-light dark:bg-[rgba(10,119,35,0.40)]'
            : 'bg-warning-dark',
        )}
      >
        {seconds ? secondsToTime(seconds) : 'Send'}
      </div>
    )
  }

  return (
    <div
      className={classNames(
        'text-secondary-300 overflow-hidden text-sm',
        !message.isOpened && 'text-title font-medium',
      )}
    >
      {content}
    </div>
  )
}
